import { lazy } from "react";
import { ProjectsPage, DiscoverRoutePage } from "./Projects/Projects";
import { HomePage } from "./Home/HomePage";
import { OrganizationPage } from "./Organization";
import { FF_HOMEPAGE, isFF } from "../utils/feature-flags";
import { pages } from "@humansignal/app-common";
import { routes as extensionRoutes } from "@afriannotate/extensions";

// Route-level lazy-loading of the ADMIN / MANAGER pages. Annotators never
// open these, so their code should not download on first paint. Each page
// below is an OBJECT DESCRIPTOR that carries its ROUTE METADATA (path /
// exact / title / context) EAGERLY — copied verbatim from the page's own
// static props — while only the component is lazy-loaded via ``import()``.
// pageSetToRoutes (utils/routeHelpers) reads these props synchronously to
// build the route table; React.lazy would otherwise drop them. The lazy
// components resolve under the <Suspense> boundary in ProjectRoutes.
//
// HomePage / DiscoverRoutePage / ProjectsPage stay EAGER (first paint /
// annotator-critical). Project sub-pages inside Projects/Projects.jsx are
// deliberately left untouched in this pass.

const CreateOrganizationPage = {
  title: "New Organization",
  path: "/organization/new",
  exact: true,
  component: lazy(() =>
    import("./Organization/CreateOrganizationPage/CreateOrganizationPage").then((m) => ({
      default: m.CreateOrganizationPage,
    })),
  ),
};

const PlatformPage = {
  title: "Platform",
  path: "/platform",
  exact: false,
  component: lazy(() => import("./Platform").then((m) => ({ default: m.PlatformPage }))),
};

const OrganizationsPage = {
  title: "Organizations",
  path: "/organizations",
  exact: true,
  component: lazy(() =>
    import("./Platform/PlatformOrgsPage/PlatformOrgsPage").then((m) => ({
      default: m.OrganizationsPage,
    })),
  ),
};

const ProjectTemplatesPage = {
  title: "My templates",
  path: "/project-templates",
  exact: true,
  component: lazy(() =>
    import("./ProjectTemplates/ProjectTemplatesPage").then((m) => ({
      default: m.ProjectTemplatesPage,
    })),
  ),
};

const UserProfilePage = {
  title: "Profile",
  // Deliberately ``:userId`` (not ``:id``) — see UserProfilePage source.
  // Non-exact so /users/:id, /users/:id/settings, /users/:id/analytics/:sub
  // all resolve to this self-routing shell.
  path: "/users/:userId(\\d+)",
  exact: false,
  component: lazy(() => import("./UserProfile/UserProfilePage").then((m) => ({ default: m.UserProfilePage }))),
};

const OfflineStoragePage = {
  title: "Offline storage",
  path: "/offline-storage",
  exact: true,
  component: lazy(() =>
    import("./OfflineStorage/OfflineStoragePage").then((m) => ({
      default: m.OfflineStoragePage,
    })),
  ),
};

const TestingChecklistPage = {
  title: "Testing checklist",
  path: "/testing-checklist",
  exact: true,
  component: lazy(() =>
    import("./TestingChecklist/TestingChecklistPage").then((m) => ({
      default: m.TestingChecklistPage,
    })),
  ),
};

const AccountSettingsRedirect = {
  path: "/user/account",
  exact: false,
  component: lazy(() => import("./UserProfile/UserProfilePage").then((m) => ({ default: m.AccountSettingsRedirect }))),
};

// HomePage is always in the route table now. The flag-based
// gating (`isFF(FF_HOMEPAGE)`) used to read window.APP_SETTINGS at
// module-load time, which races on cold boot in the desktop app
// (the shell finishes fetching `/api/app-config/` before main.js
// runs, but `Pages` is evaluated EAGERLY when this module loads —
// so under some timing the flag check returned false even when the
// user has it on, leaving `/` unmatched and the page blank). The
// sidebar's Home link is still gated separately, and the shell
// redirects unauthenticated / non-flagged users to /projects/
// before the SPA even boots, so always-registering HomePage
// doesn't change the product flow.
export const Pages = [
  HomePage,
  DiscoverRoutePage,
  ProjectsPage,
  CreateOrganizationPage,
  OrganizationPage,
  PlatformPage,
  OrganizationsPage,
  ProjectTemplatesPage,
  UserProfilePage,
  OfflineStoragePage,
  TestingChecklistPage,
  AccountSettingsRedirect,
  // Pages contributed by an installed extension package (empty by default).
  ...(extensionRoutes ?? []),
].filter(Boolean);
